// Smart Daara - School Admin Dashboard
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { courseAPI, tenantAPI } from '../../lib/api';
import { buildTenantPath } from '../../lib/routing';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  GraduationCap, Users, BookOpen, FolderOpen, TrendingUp, Plus,
  ArrowRight, BarChart3, Clock, CheckCircle, Pencil
} from 'lucide-react';

export default function SchoolDashboard() {
  const { user } = useAuth();
  const { t, language, getText } = useLanguage();
  const navigate = useNavigate();
  const slug = user?.tenant_slug;
  const p = (sub) => buildTenantPath(slug, sub);
  
  const [stats, setStats] = useState({
    students: 0,
    teachers: 0,
    classes: 0,
    courses: 0,
    lessons: 0
  });
  const [recentCourses, setRecentCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?.tenant_id) return;
    
    try {
      // Load stats
      const statsResponse = await tenantAPI.getStats(user.tenant_id);
      setStats(statsResponse.data);

      // Load recent courses
      const coursesResponse = await courseAPI.listCourses({ tenant_id: user.tenant_id });
      setRecentCourses((coursesResponse.data || []).slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: t('totalStudents'),
      value: stats.students || 0,
      icon: GraduationCap,
      color: 'bg-emerald-100 text-emerald-700',
      link: p('school/students')
    },
    {
      title: t('totalTeachers'),
      value: stats.teachers || 0,
      icon: Users,
      color: 'bg-amber-100 text-amber-700',
      link: p('school/teachers')
    },
    {
      title: t('totalClasses'),
      value: stats.classes || 0,
      icon: FolderOpen,
      color: 'bg-blue-100 text-blue-700',
      link: p('school/classes')
    },
    {
      title: t('totalCourses'),
      value: stats.courses || 0,
      icon: BookOpen,
      color: 'bg-purple-100 text-purple-700',
      link: p('school/courses')
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="school-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">
            {t('welcome')}, {user?.first_name}!
          </h1>
          <p className="text-slate-500">{t('schoolOverview')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(p('school/courses'))}>
            <BookOpen className="w-4 h-4 mr-2" />
            {t('courses')}
          </Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={() => navigate(p('school/students'))}>
            <Plus className="w-4 h-4 mr-2" />
            {t('addStudent')}
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card 
            key={index} 
            className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate(stat.link)}
            data-testid={`stat-card-${index}`}
          >
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>
              <div className="mt-4">
                <p className="text-2xl font-outfit font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1">{stat.title}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Courses */}
        <Card className="lg:col-span-2 border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-outfit">{t('recentCourses')}</CardTitle>
              <CardDescription>{t('latestCoursesAdded')}</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate(p('school/courses'))}>
              {t('viewAll')}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentCourses.length > 0 ? (
                recentCourses.map((course, index) => (
                  <div 
                    key={course.id} 
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 group"
                  >
                    <div 
                      className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 cursor-pointer"
                      onClick={() => navigate(p(`school/courses/${course.id}`))}
                    >
                      <BookOpen className="w-6 h-6 text-emerald-700" />
                    </div>
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => navigate(p(`school/courses/${course.id}`))}
                    >
                      <p className="font-medium text-slate-900 truncate">
                        {getText(course, 'title')}
                      </p>
                      <p className="text-sm text-slate-500">
                        {course.total_lessons || 0} {t('lessons').toLowerCase()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {course.is_published ? (
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                          {t('published')}
                        </span>
                      ) : (
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                          {t('draft')}
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(p(`school/courses/${course.id}/edit`));
                        }}
                        data-testid={`edit-course-${index}`}
                      >
                        <Pencil className="w-4 h-4 text-slate-600" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>{t('noCoursesYet')}</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={() => navigate(p('school/courses/new'))}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('createCourse')}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-outfit">{t('quickActions')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => navigate(p('school/classes'))}
            >
              <FolderOpen className="w-4 h-4 mr-3 text-emerald-600" />
              {t('manageClasses')}
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => navigate(p('school/courses/new'))}
            >
              <BookOpen className="w-4 h-4 mr-3 text-amber-600" />
              {t('addCourse')}
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => navigate(p('school/students'))}
            >
              <GraduationCap className="w-4 h-4 mr-3 text-blue-600" />
              {t('enrollStudent')}
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => navigate(p('school/teachers'))}
            >
              <Users className="w-4 h-4 mr-3 text-purple-600" />
              {t('addTeacher')}
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => navigate(p('school/parents'))}
            >
              <Users className="w-4 h-4 mr-3 text-indigo-600" />
              {t('addParent')}
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => navigate(p('school/subscription'))}
            >
              <BarChart3 className="w-4 h-4 mr-3 text-slate-600" />
              {t('viewSubscription')}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Activity Summary */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-outfit">{t('activitySummary')}</CardTitle>
          <CardDescription>{t('weekStatistics')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <Clock className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
              <p className="text-2xl font-bold text-slate-900">24h</p>
              <p className="text-sm text-slate-500">{t('learningTime')}</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-amber-600" />
              <p className="text-2xl font-bold text-slate-900">{stats.lessons || 0}</p>
              <p className="text-sm text-slate-500">{t('availableLessons')}</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-blue-600" />
              <p className="text-2xl font-bold text-slate-900">85%</p>
              <p className="text-sm text-slate-500">{t('completionRate')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
